import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import * as Dialog from "@radix-ui/react-dialog";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router";
import { AlertCircle, Archive, Bookmark, Trash2 } from "lucide-react";
import {
  deleteOrder,
  getOrder,
  transitionOrderStatus,
  updateOrder,
} from "@/api/orders";
import { getCustomFields, getPlanningSettings } from "@/api/settings";
import { Button } from "@/components/ui/Button";
import { DateInput } from "@/components/ui/DateInput";
import { IconButton } from "@/components/ui/IconButton";
import { Toggle } from "@/components/ui/Toggle";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { Skeleton } from "@/components/ui/Skeleton";
import {
  applyApiErrorToForm,
  extractApiErrorMessage,
  readApiErrorStatus,
} from "@/lib/apiErrors";
import { formatDateTime } from "@/lib/format";
import { useTenantTimezone } from "@/features/settings/hooks/useTenantTimezone";
import { useCostUnit } from "@/hooks/useCostUnit";
import { toastSuccess } from "@/lib/toast";
import { cn } from "@/lib/cn";
import { normalizePriority } from "@/lib/priority";
import { PriorityField } from "./PriorityField";
import { RatingStars } from "@/features/reports/components/RatingStars";
import { useAuthStore } from "@/store/auth";
import type { Order, OrderStatus, UpdateOrderPayload } from "@/types/orders";
import { CustomerTrackingSection } from "./CustomerTrackingSection";
import { ParcelsEditSection } from "./ParcelsEditSection";
import { ParcelRowsEditor } from "./ParcelRowsEditor";
import { formatParcelWeight, sumParcelWeight } from "./shared/parcelTotals";
import { SaveAsTemplateModal } from "./SaveAsTemplateModal";
import { OrderAuditColumn } from "./OrderAuditColumn";
import { OrderRouteBox } from "./OrderRouteBox";
import { OrderViewFacts } from "./OrderViewFacts";
import { OrderViewWhere } from "./OrderViewWhere";
import { AddressMapBlock } from "./shared/AddressMapBlock";
import { CargoNumbersRow } from "./shared/CargoNumbersRow";
import {
  ConfirmationBlock,
  type ConfirmationOption,
} from "./shared/ConfirmationBlock";
import { ModalColumn, ModalSubheading } from "./shared/ModalColumn";
import { ModalThreeColumn } from "./shared/ModalThreeColumn";
import { TagsInput } from "./shared/TagsInput";
import { useUnsavedGuard } from "./shared/useUnsavedGuard";

// Dispatcher-appropriate subset of the backend order state machine
// (`OrderStatusMachine.TRANSITIONS`). The backend allows more transitions, but
// most are driver/mobile-set (in_route is set by route dispatch; visited /
// confirmed / rejected / skipped are driver actions). A dispatcher only
// manually archives or re-queues (→ new). The endpoint enforces the full
// machine; this map filters to the buttons a dispatcher should see.
const TRANSITIONS: Record<OrderStatus, OrderStatus[]> = {
  new: ["archived"],
  in_route: [],
  visited: [],
  confirmed: ["archived"],
  rejected: ["new", "archived"],
  skipped: ["new", "archived"],
  archived: [],
};

// Order-status pill palette, keyed to the real backend `Order.Status` values
// (mirrors OrdersPage / route-stop colours).
const STATUS_STYLE: Record<
  OrderStatus,
  { bg: string; text: string; dot: string; border: string }
> = {
  new: {
    bg: "bg-blue-50",
    text: "text-blue-700",
    dot: "bg-blue-400",
    border: "border-blue-200",
  },
  in_route: {
    bg: "bg-orange-50",
    text: "text-orange-700",
    dot: "bg-orange-400",
    border: "border-orange-200",
  },
  visited: {
    bg: "bg-sky-50",
    text: "text-sky-700",
    dot: "bg-sky-400",
    border: "border-sky-200",
  },
  confirmed: {
    bg: "bg-emerald-50",
    text: "text-emerald-700",
    dot: "bg-emerald-500",
    border: "border-emerald-200",
  },
  rejected: {
    bg: "bg-rose-50",
    text: "text-rose-700",
    dot: "bg-rose-500",
    border: "border-rose-200",
  },
  skipped: {
    bg: "bg-amber-50",
    text: "text-amber-700",
    dot: "bg-amber-400",
    border: "border-amber-200",
  },
  archived: {
    bg: "bg-gray-100",
    text: "text-gray-500",
    dot: "bg-gray-400",
    border: "border-gray-200",
  },
};

const editSchema = z.object({
  // BUG-409: the API serialises a blank external_id as `null`, and `.optional()`
  // accepts only `string | undefined` — so a null seeded from the API failed
  // validation with the raw zod message «Expected string, received null» and
  // blocked the save entirely. `.nullish()` = `.nullable().optional()`.
  // Nullish for the same reason as external_id below: the API serialises a blank title as null.
  title: z.string().nullish(),
  external_id: z.string().nullish(),
  order_type: z.string().optional(),
  delivery_type: z.enum(["delivery", "pickup"]).optional(),
  address: z.string().min(1, "Required"),
  // BUG-409: `lat`/`lon` are `DecimalField(null=True)` on the backend, so an
  // imported order that FAILED to geocode serialises them as `null` — exactly
  // the case the user reported. `.optional()` accepts only `string | undefined`,
  // so the null seeded from the API failed with «Invalid input» and blocked the
  // save. `.nullish()` = `.nullable().optional()`; "" still passes (the refine
  // treats `Number("")` as 0) and a non-numeric string still fails.
  lat: z
    .string()
    .refine((v) => !isNaN(Number(v)), "Must be a number")
    .nullish()
    .or(z.literal("")),
  lon: z
    .string()
    .refine((v) => !isNaN(Number(v)), "Must be a number")
    .nullish()
    .or(z.literal("")),
  delivery_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Must be YYYY-MM-DD"),
  time_window_start: z.string().nullable().optional(),
  time_window_end: z.string().nullable().optional(),
  weight_kg: z
    .string()
    .refine((v) => v === "" || Number(v) >= 0, "Must be non-negative")
    .optional(),
  volume_m3: z
    .string()
    .refine((v) => v === "" || Number(v) >= 0, "Must be non-negative")
    .optional(),
  pallets: z
    .string()
    .refine((v) => v === "" || Number(v) >= 0, "Must be non-negative")
    .optional(),
  service_time_min: z
    .number()
    .int()
    .min(0, "Must be non-negative")
    .max(240)
    .optional()
    .or(z.nan()),
  // TASK-4833 — three levels, never a number.
  priority: z.enum(["low", "medium", "high"]).optional(),
  strict_time_window: z.boolean().optional(),
  customer_name: z.string().optional(),
  customer_phone: z.string().optional(),
  customer_email: z.string().optional(),
  notes: z.string().optional(),
  tags: z.string().optional(),
  item_cost: z
    .string()
    .refine((v) => v === "" || Number(v) >= 0, "Must be non-negative")
    .optional(),
  /** True when the user wants to override the tenant-level confirmation setting */
  override_confirmation: z.boolean().optional(),
  /**
   * The combined confirmation option selected by the user when override is on.
   * "disabled" maps to require_confirmation=false; others map to require_confirmation=true.
   */
  confirmation_option: z
    .enum(["photo", "signature", "both", "disabled"])
    .optional(),
  /**
   * Per-order confirmation radius in metres. Optional; blank maps to no
   * override (null). Non-negative integer when provided.
   */
  confirmation_radius_m: z
    .number()
    .int()
    .min(0, "Must be non-negative")
    .optional()
    .or(z.nan()),
  /**
   * Explicit per-parcel rows — the SAME shape the create form uses, so
   * {@link ParcelRowsEditor} drives both surfaces unchanged.
   *
   * Held in FORM STATE and saved with the order in one PATCH, rather than
   * persisted row-by-row like the read view's {@link ParcelsEditSection}. See
   * the note on {@link OrderDetailModal} for why the two differ.
   */
  parcels: z
    .array(
      z.object({
        code: z.string().optional(),
        description: z.string().optional(),
        weight_kg: z
          .string()
          .refine((v) => v === "" || Number(v) >= 0, "Must be non-negative")
          .optional(),
      }),
    )
    .optional(),
});

type EditFormValues = z.infer<typeof editSchema>;

/**
 * Backend field name → edit-form field name, for {@link applyApiErrorToForm}.
 *
 * BUG-464: all three mutations in this modal (save, status transition, delete) previously
 * showed `err.message`, which for an axios failure is the transport string
 * ("Request failed with status code 400") rather than the server's `detail`. They share one
 * form, so they share one map — a per-field error (e.g. a rejected `delivery_date` or an
 * address the geocoder refused) lands on its own input, and everything else goes to `root`.
 *
 * Backend fields absent from this map deliberately fall through to `root` rather than being
 * swallowed — see `applyApiErrorToForm`.
 */
const ORDER_FIELD_MAP: Record<string, keyof EditFormValues & string> = {
  title: "title",
  external_id: "external_id",
  order_type: "order_type",
  delivery_type: "delivery_type",
  address: "address",
  lat: "lat",
  lon: "lon",
  delivery_date: "delivery_date",
  time_window_start: "time_window_start",
  time_window_end: "time_window_end",
  weight_kg: "weight_kg",
  volume_m3: "volume_m3",
  pallets: "pallets",
  service_time_min: "service_time_min",
  priority: "priority",
  strict_time_window: "strict_time_window",
  customer_name: "customer_name",
  customer_phone: "customer_phone",
  customer_email: "customer_email",
  notes: "notes",
  tags: "tags",
  item_cost: "item_cost",
  confirmation_radius_m: "confirmation_radius_m",
};

function orderToFormValues(order: Order): EditFormValues {
  const hasOverride = order.require_confirmation !== null;
  // Map stored fields to the combined "option" value shown in the select
  let confirmationOption: ConfirmationOption = "photo";
  if (hasOverride) {
    if (order.require_confirmation === false) {
      confirmationOption = "disabled";
    } else {
      confirmationOption = order.confirmation_mode ?? "photo";
    }
  }

  // BUG-409: coalesce every nullable/latent-nullable string field to "" when
  // seeding the form. `external_id` is genuinely `null` for orders without an
  // external number, and `lat`/`lon` are genuinely `null` for an imported order
  // that failed to geocode — both are `null=True` in `orders/models.py`. The
  // remaining siblings are `blank=True`/`default=` (never null) today but are
  // hardened for consistency with time_window_* which already did this.
  return {
    title: order.title ?? "",
    external_id: order.external_id ?? "",
    order_type: order.order_type ?? "",
    // BUG-460: `??` only catches null/undefined. The API returns `delivery_type: ""` for some
    // orders (6 of 100 on the demo tenant), and `""` sailed through into a `z.enum` that rejects
    // it — so `handleSubmit` never fired the mutation and saving did nothing at all. `||` also
    // catches the empty string.
    delivery_type: order.delivery_type || "delivery",
    address: order.address,
    lat: order.lat ?? "",
    lon: order.lon ?? "",
    delivery_date: order.delivery_date,
    time_window_start: order.time_window_start ?? "",
    time_window_end: order.time_window_end ?? "",
    weight_kg: order.weight_kg ?? "",
    volume_m3: order.volume_m3 ?? "",
    pallets: order.pallets ?? "",
    service_time_min: order.service_time_min ?? undefined,
    priority: normalizePriority(order.priority),
    strict_time_window: order.strict_time_window,
    customer_name: order.customer_name ?? "",
    customer_phone: order.customer_phone ?? "",
    customer_email: order.customer_email ?? "",
    notes: order.notes ?? "",
    tags: order.tags?.join(", ") ?? "",
    item_cost: order.item_cost ?? "",
    override_confirmation: hasOverride,
    confirmation_option: confirmationOption,
    confirmation_radius_m: order.confirmation_radius_m ?? undefined,
    /*
      Seeded from the saved parcels so «Редактировать» opens on the order's real
      list rather than an empty one — an empty seed would read as "this order has
      no parcels" and, on save, REPLACE the real ones with nothing.

      `formatParcelWeight` rather than the raw string: DRF sends a
      `Decimal(_, 3)` as «2.500», which is the same value the read view already
      renders as «2.5» (see `ParcelsEditSection.draftOf`). Seeding the raw form
      would make every untouched row read differently here than there, and would
      make the computed total render «12.000».
    */
    parcels: (order.parcels ?? []).map((parcel) => ({
      code: parcel.code ?? "",
      description: parcel.description ?? "",
      weight_kg:
        parcel.weight_kg == null || parcel.weight_kg.trim() === ""
          ? ""
          : formatParcelWeight(Number(parcel.weight_kg)),
    })),
  };
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orderId: number;
}

/**
 * «Заказ ORD-…» — the order detail modal, rebuilt as three columns (TASK-4830).
 *
 * **The edit path (owner ruling, 2026-08-23).** «что обычные что постоянные
 * заказы форма редактирования должны быть такие же как при создании по макету».
 * Editing stays INSIDE this modal behind `editMode`, and the edit body renders
 * the TASK-4829 create layout — the same three columns, the same field order,
 * the same shared leaf components — with values loaded.
 *
 * Why in place rather than opening `CreateOrderModal` in an edit mode: this
 * form carries order-only concerns that the create modal has no notion of —
 * BUG-409's `.nullish()` seeding of real API nulls, BUG-460's `""`
 * `delivery_type` coalescing, custom-field prefill, status transitions and
 * `invalidateMapSurfaces()`. Threading a `mode` prop through the create modal
 * would recreate exactly the seam that let the two address implementations
 * drift (§1.1.1). Sharing the *leaf* components instead means the two layouts
 * cannot drift while each host keeps its own field mapping.
 */
export function OrderDetailModal({ open, onOpenChange, orderId }: Props) {
  const { t, i18n } = useTranslation();
  // TASK-5043: every instant in this modal reads in the tenant's zone, not the
  // dispatcher's browser zone. Safe as a hook — the modal is driven by
  // `useQuery` and both its tests wrap it in a `QueryClientProvider`.
  const tenantTz = useTenantTimezone();
  const costUnit = useCostUnit();
  const queryClient = useQueryClient();
  const [editMode, setEditMode] = useState(false);
  const [pendingTransition, setPendingTransition] =
    useState<OrderStatus | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmCancelEdit, setConfirmCancelEdit] = useState(false);
  const [saveTemplateOpen, setSaveTemplateOpen] = useState(false);
  const [customValues, setCustomValues] = useState<Record<string, string>>({});

  const user = useAuthStore((s) => s.user);
  const canSaveTemplate =
    user?.role === "dispatcher" || user?.role === "company_admin";

  // BUG-498: `error` and `refetch` are read below to render a real failure
  // state. Before that, the body had only an `isLoading` branch and an
  // `order &&` branch — so a rejected detail query (dev served 404 for every
  // one of ~1 850 archived orders) fell through BOTH and painted an empty
  // modal: a title, a close button, nothing else. That silence is what let the
  // backend defect live unnoticed; without this branch the next 404 would be
  // exactly as invisible.
  const {
    data: order,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ["order", orderId],
    queryFn: () => getOrder(orderId),
    enabled: open && orderId > 0,
  });

  // A 404 means the order genuinely is not there — retrying cannot help, so the
  // UI says so and offers no retry. Anything else (500, network, timeout) is
  // potentially transient and gets a Retry action.
  const notFound = readApiErrorStatus(error) === 404;

  const { data: customFields = [] } = useQuery({
    queryKey: ["custom-fields"],
    queryFn: getCustomFields,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // Tenant planning settings — source of the `parcels_enabled` feature flag.
  const { data: planningSettings } = useQuery({
    queryKey: ["settings-planning"],
    queryFn: getPlanningSettings,
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });
  const parcelsEnabled = planningSettings?.parcels_enabled ?? false;
  const canEditParcels =
    user?.role === "dispatcher" || user?.role === "company_admin";
  /**
   * Whether parcel codes are derived from the order number. Read straight off
   * the same query as `parcels_enabled`, so a company admin flipping either
   * setting at `/settings` is reflected without a reload (BUG-523).
   */
  const barcodesFromOrderNumber =
    planningSettings?.barcode_generation_mode === "order_number";

  // Prefill custom values when order loads or edit mode starts
  useEffect(() => {
    if (order) {
      setCustomValues(
        Object.fromEntries(
          Object.entries(order.custom_fields ?? {}).map(([k, v]) => [
            k,
            String(v),
          ]),
        ),
      );
    }
  }, [order]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isDirty },
    setError,
  } = useForm<EditFormValues>({
    resolver: zodResolver(editSchema),
    defaultValues: order ? orderToFormValues(order) : {},
  });

  const watchedAddress = watch("address");
  const [watchedLatStr, watchedLonStr] = watch(["lat", "lon"]);
  const overrideConfirmation = watch("override_confirmation");
  const confirmationOption = watch("confirmation_option");
  const confirmationRadius = watch("confirmation_radius_m");
  const deliveryType = watch("delivery_type");
  const watchedParcels = watch("parcels");

  /*
    The weight rule, carried over from «Новый заказ» UNCHANGED (owner,
    2026-08-25). It lives in one derived boolean here for the same reason it does
    there: the field, the submitted payload and the footer hint all read it, and
    three parallel derivations are how they drift into disagreeing about the same
    number.

    A weight typed while the flag was OFF is kept in form state and merely stops
    being displayed or submitted while the sum applies — never cleared, so
    flipping the flag back restores it exactly.
  */
  const parcelWeightTotal = sumParcelWeight(watchedParcels);
  const parcelCount = watchedParcels?.length ?? 0;
  const computedWeight = formatParcelWeight(parcelWeightTotal);
  /**
   * Whether the weight is actually DERIVED right now.
   *
   * The feature flag alone is NOT the condition (owner, 2026-08-25): a tenant
   * can have parcels enabled and still hold an order nobody itemised, and that
   * dispatcher must be able to state a weight. The test is the WEIGHTED total,
   * not the row count — four rows carrying only barcodes say nothing about how
   * heavy the order is.
   */
  const weightIsComputed = parcelsEnabled && parcelWeightTotal > 0;

  const handleEditClick = () => {
    if (order) reset(orderToFormValues(order));
    setEditMode(true);
  };

  /** Leaves edit mode, restoring the loaded order's values. */
  const discardEdit = () => {
    reset(order ? orderToFormValues(order) : {});
    setEditMode(false);
  };

  /**
   * The unsaved-changes guard, shared with every Sprint 186 form surface.
   *
   * It also carries the BUG-497 obligation: `DateInput` renders its calendar
   * through a portal, so a click inside it is not a DOM descendant of the
   * dialog and reads as an "outside" click. `guardInteractOutside` recognises a
   * portalled popover and refuses to tear the dialog down mid-interaction.
   */
  const guard = useUnsavedGuard({
    isDirty: editMode && isDirty,
    onConfirmedClose: () => {
      setEditMode(false);
      onOpenChange(false);
    },
  });

  // BUG-405: refresh every Routes-map surface an order change can affect —
  // the unassigned/free-orders pool and the per-day route stops/geometry —
  // so a date/status edit or delete shows on the map without a manual reload.
  async function invalidateMapSurfaces() {
    await Promise.all(
      [
        ["unassigned-orders"],
        ["routes-map"],
        ["route-stops-map"],
        ["route-geometry-map"],
      ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
    );
  }

  const updateMutation = useMutation({
    mutationFn: (payload: UpdateOrderPayload) => updateOrder(orderId, payload),
    onSuccess: async (updated) => {
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      // The change log gains an entry on every save, so it must refetch or the
      // third column would keep showing pre-edit history.
      await queryClient.invalidateQueries({
        queryKey: ["audit-log", "Order", orderId],
      });
      // BUG-405: a date/status change can move the order in/out of the Routes
      // map's day, so refresh the map's unassigned pool + route surfaces too.
      await invalidateMapSurfaces();
      reset(orderToFormValues(updated));
      setEditMode(false);
      toastSuccess(t("toast.orderSaved"));
    },
    onError: (err: unknown) => {
      applyApiErrorToForm(err, t, setError, ORDER_FIELD_MAP);
    },
  });

  const transitionMutation = useMutation({
    mutationFn: (target: OrderStatus) => transitionOrderStatus(orderId, target),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      await queryClient.invalidateQueries({ queryKey: ["order", orderId] });
      await queryClient.invalidateQueries({
        queryKey: ["audit-log", "Order", orderId],
      });
      // BUG-405: status change affects map unassigned pool + route surfaces.
      await invalidateMapSurfaces();
      setPendingTransition(null);
      toastSuccess(t("toast.statusUpdated"));
    },
    onError: (err: unknown) => {
      applyApiErrorToForm(err, t, setError, ORDER_FIELD_MAP);
      setPendingTransition(null);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => deleteOrder(orderId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["orders"] });
      // Refresh the Routes map's free-orders layer so deleted pins vanish
      // without a reload (BUG-398/405).
      await invalidateMapSurfaces();
      onOpenChange(false);
    },
    onError: (err: unknown) => {
      applyApiErrorToForm(err, t, setError, ORDER_FIELD_MAP);
      setConfirmDelete(false);
    },
  });

  function onSubmit(values: EditFormValues) {
    // Collect non-empty custom field values
    const builtCustomFields: Record<string, string> = {};
    for (const [key, val] of Object.entries(customValues)) {
      if (val.trim()) builtCustomFields[key] = val;
    }

    // Resolve confirmation override fields
    let requireConfirmation: boolean | null = null;
    let confirmationMode: "photo" | "signature" | "both" | null = null;
    if (values.override_confirmation) {
      if (values.confirmation_option === "disabled") {
        requireConfirmation = false;
        confirmationMode = null;
      } else {
        requireConfirmation = true;
        confirmationMode = values.confirmation_option ?? "photo";
      }
    }

    /*
      The rows that actually say something — the same filter the create form
      applies, so an untouched blank row appended by «+ Место» is not persisted
      as an empty parcel.
    */
    const parcelRows = (values.parcels ?? []).filter(
      (row) =>
        (row.code ?? "").trim() !== "" ||
        (row.description ?? "").trim() !== "" ||
        (row.weight_kg ?? "").trim() !== "",
    );
    const submitWeightTotal = sumParcelWeight(parcelRows);
    /*
      THE DATA-LOSS GUARD, carried over verbatim in intent from the create form.

      The derived weight wins ONLY when there is something to derive it from.
      Writing this as `parcelsEnabled ? computed : typed` — the shape that
      shipped and had to be fixed in create — sends `undefined` whenever the
      feature is on but no row carries a weight, SILENTLY DROPPING a weight the
      dispatcher typed into what was, in that state, an ordinary editable field.
      This mirrors `weightIsComputed` exactly, so what the dispatcher sees in the
      field is what gets sent.
    */
    const weightForSubmit =
      parcelsEnabled && submitWeightTotal > 0
        ? formatParcelWeight(submitWeightTotal)
        : values.weight_kg || undefined;

    const payload: UpdateOrderPayload = {
      title: values.title ?? "",
      external_id: values.external_id ?? "",
      order_type: values.order_type,
      delivery_type: values.delivery_type,
      address: values.address,
      lat: values.lat || undefined,
      lon: values.lon || undefined,
      delivery_date: values.delivery_date,
      time_window_start: values.time_window_start || null,
      time_window_end: values.time_window_end || null,
      weight_kg: weightForSubmit,
      volume_m3: values.volume_m3 || undefined,
      pallets: values.pallets || undefined,
      service_time_min:
        values.service_time_min != null && !isNaN(values.service_time_min)
          ? values.service_time_min
          : undefined,
      priority: values.priority ?? undefined,
      strict_time_window: values.strict_time_window,
      customer_name: values.customer_name,
      customer_phone: values.customer_phone,
      customer_email: values.customer_email,
      notes: values.notes,
      tags: values.tags
        ? values.tags
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean)
        : [],
      item_cost: values.item_cost || undefined,
      custom_fields: builtCustomFields,
      require_confirmation: requireConfirmation,
      confirmation_mode: confirmationMode,
      // Blank/omitted → null (no override).
      confirmation_radius_m:
        values.confirmation_radius_m != null &&
        !isNaN(values.confirmation_radius_m)
          ? values.confirmation_radius_m
          : null,
    };

    /*
      Parcels ride along in the SAME PATCH, so «Сохранить» commits the order and
      its list together and «Отмена» genuinely discards both.

      `parcels` is DESTRUCTIVE on the backend — `OrderUpdateSerializer.update`
      deletes the order's existing rows and recreates them from the list, and
      omitting the key leaves them untouched. So the key is sent ONLY when the
      feature is on: with parcels disabled this form never showed the list, has
      no informed opinion about it, and sending `[]` would wipe an order's real
      parcels as a side effect of editing its address while the flag happened to
      be off.

      When the feature IS on, an empty list is sent deliberately — that is how a
      dispatcher removes the last parcel, and it is the state the form is
      actually showing them.
    */
    if (parcelsEnabled) {
      payload.parcels = parcelRows.map((row) => ({
        code: (row.code ?? "").trim() || undefined,
        description: (row.description ?? "").trim() || undefined,
        weight_kg: (row.weight_kg ?? "").trim() || undefined,
      }));
    }

    updateMutation.mutate(payload);
  }

  function formatOrderType(type: string | undefined): string {
    if (!type) return "—";
    if (type === "one_time") return t("orders.detail.oneTime");
    if (type === "recurring") return t("orders.detail.recurring");
    return type.charAt(0).toUpperCase() + type.slice(1);
  }

  /**
   * Human-readable POD method. Prefers i18n keyed off `pod_method`, falling
   * back to the backend-provided `pod_method_display` string.
   */
  function formatPodMethod(o: Order): string {
    const key = `orders.detail.podMethodValue.${o.pod_method}`;
    const translated = t(key);
    return translated === key ? o.pod_method_display : translated;
  }

  /**
   * Locale-aware timestamp formatter for feedback dates, in the TENANT's zone
   * (TASK-5043).
   *
   * Keeps its own `Intl` call rather than using the shared `formatDateTime`:
   * this label wants a 2-digit DAY («05 окт.»), which `formatDateTime`'s
   * `day: "numeric"` does not produce. The locale pinning to `ru-RU`/`en-GB` is
   * also deliberate and predates this task — it forces a 24-hour clock even
   * when the UI language resolves to a region that would use AM/PM.
   */
  function formatFeedbackDate(iso: string): string {
    return new Date(iso).toLocaleString(
      i18n.language === "ru" ? "ru-RU" : "en-GB",
      {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        timeZone: tenantTz,
      },
    );
  }

  const st = order ? (STATUS_STYLE[order.status] ?? STATUS_STYLE.new) : null;
  const nextTransitions = order ? (TRANSITIONS[order.status] ?? []) : [];
  const canArchive = nextTransitions.includes("archived");
  /**
   * Transitions the FOOTER offers — every one except archive, which is a header
   * button (owner, 2026-08-24: «Архив» was rendering in both places and doing the
   * same thing). Empty for `new`/`confirmed`, so the footer section drops out; for
   * `rejected`/`skipped` it keeps «→ Новый», which exists nowhere else.
   */
  const otherTransitions = nextTransitions.filter((s) => s !== "archived");

  const inputCls =
    // `h-10` (40px), the modal's single control height — shared with the
    // segmented fields and the footer buttons. The old `py-2 2xl:py-2.5`
    // grew the inputs to 42px on wide screens only, so the modal was
    // internally consistent at 1280 and a step off at 1440.
    "h-10 w-full rounded-xl border border-gray-200 px-3 text-sm text-gray-900 outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary";
  const labelCls = "mb-1 block text-xs font-semibold text-gray-700";

  /*
    ─────────────────── the two MOVING fields ───────────────────

    «Стоимость товара» and «Заметки для водителя» sit in DIFFERENT columns in the
    two mockups, exactly as they do in «Новый заказ»: with parcels ON they belong
    to column 1 (ЗАКАЗ), with parcels OFF to column 2 (ГРУЗ), where the space the
    parcels block would have taken is free.

    Defined ONCE here and called from exactly one column per state. Rendering
    them in both and hiding one with CSS is the BUG-504/508 defect this project
    has already shipped three times: a `hidden` copy still contributes its label
    to the accessibility tree, so a screen-reader user hears «Стоимость товара»
    twice and a duplicate-name audit fails. One definition, one mount, no
    duplicates possible.
  */
  /*
    «Внешний ID» and «Тип доставки» keep the same ids and labels in both
    layouts; only which ROW they sit on changes. Defined once for the same
    reason as the two cross-column movers — one mount, so a duplicate accessible
    name is impossible by construction rather than by review.
  */
  const externalIdField = (
    <div className="min-w-0">
      <label htmlFor="edit-order-external-id" className={labelCls}>
        {t("orders.detail.externalId")}
      </label>
      <input
        id="edit-order-external-id"
        {...register("external_id")}
        className={inputCls}
      />
      {errors.external_id && (
        <p className="mt-1 text-xs text-brand-danger">
          {errors.external_id.message}
        </p>
      )}
    </div>
  );

  const deliveryTypeField = (
    <div className="min-w-0">
      <span className={labelCls} id="edit-order-delivery-type-label">
        {t("orders.delivery_type")}
      </span>
      <div
        role="radiogroup"
        aria-labelledby="edit-order-delivery-type-label"
        className="flex overflow-hidden rounded-xl border border-gray-200"
      >
        {(["delivery", "pickup"] as const).map((value) => (
          <label
            key={value}
            // `has-[:checked]:bg-brand-primary` — the dealer's RUNTIME token, so
            // the chosen half follows the tenant's palette. Never a fixed dark
            // neutral, which is the §10.10 selected-state hardcode.
            className="relative flex h-10 flex-1 cursor-pointer items-center justify-center gap-1.5 text-sm font-semibold transition-colors has-[:checked]:bg-brand-primary has-[:checked]:text-white"
          >
            <input
              {...register("delivery_type")}
              type="radio"
              value={value}
              checked={deliveryType === value}
              className="sr-only"
            />
            {value === "delivery"
              ? t("orders.delivery_type_delivery")
              : t("orders.delivery_type_pickup")}
          </label>
        ))}
      </div>
      {/* BUG-460: this was the ONLY validated field in the form with no error
          block. When it held `""` the schema rejected it, `handleSubmit` never
          fired, and the dispatcher saw nothing at all — no message, no request,
          no console warning. The seed now coalesces `""`, so this should never
          appear; it is here so the next such value fails loudly instead. */}
      {errors.delivery_type && (
        <p className="mt-1 text-xs text-brand-danger">
          {errors.delivery_type.message}
        </p>
      )}
    </div>
  );

  const itemCostField = (
    <div className="min-w-0">
      <label htmlFor="edit-order-item-cost" className={labelCls}>
        {t("orders.detail.itemCost")}
      </label>
      <div className="relative">
        <input
          id="edit-order-item-cost"
          {...register("item_cost")}
          type="number"
          min="0"
          step="0.01"
          className={`${inputCls} pr-14 tabular-nums`}
        />
        {/* The currency is a suffix, not part of the value — a tenant on BYN
            and one on EUR read the same field differently. */}
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">
          {costUnit}
        </span>
      </div>
      {errors.item_cost && (
        <p className="mt-1 text-xs text-brand-danger">
          {errors.item_cost.message}
        </p>
      )}
    </div>
  );

  /**
   * `grow` — only the copy living in column 2 (parcels OFF) absorbs the leftover
   * height, because there it is the LAST thing in a `fill` column. In column 1
   * (parcels ON) it is followed by nothing that needs the slack, so it keeps its
   * intrinsic height.
   */
  const notesField = (grow: boolean) => (
    <div className={cn("flex min-h-0 flex-col", grow && "flex-1")}>
      <label htmlFor="edit-order-notes" className={labelCls}>
        {t("orders.detail.driverNotes")}
      </label>
      <textarea
        id="edit-order-notes"
        {...register("notes")}
        rows={3}
        className={cn(
          "w-full resize-none rounded-xl border border-gray-200 px-3 py-2.5 text-sm text-gray-900 outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary",
          // `flex-1` + `min-h-0`, NOT more `rows`: a taller `rows` sets an
          // intrinsic height that RAISES the grid's common row height and
          // pushes the modal past its height budget. Growing into space that
          // already exists costs nothing.
          grow ? "min-h-[5rem] flex-1" : "h-20",
        )}
      />
    </div>
  );

  /**
   * Whether to tell the user their strict-window change will not apply until the route is
   * recomputed (BUG-624).
   *
   * Only for a route that HAS been built and is still live: `planned` or `active`. A
   * completed, archived or cancelled route is history, and a warning there would be noise
   * that trains people to ignore the message when it matters.
   *
   * Driven by the form's CURRENT value rather than the saved one, so the notice appears the
   * moment the switch is flipped — which is when the user is asking the question.
   */
  const strictWindowValue = watch("strict_time_window");
  const needsRebuildNotice =
    editMode &&
    strictWindowValue === true &&
    order?.strict_time_window !== true &&
    (order?.route_status === "planned" || order?.route_status === "active");

  /**
   * Header title — «Заказ ORD-… · Название», or the generic label before the order loads.
   *
   * The NAME was missing entirely (BUG-625): the header identified the order by an id the
   * dispatcher did not choose, while the route list beside it identifies the same stops by
   * their titles («Проверка 1», «Проверка 2» …). Two names for one thing, and the card
   * showed the one nobody works with. 3821 orders on dev carry a title, so this is the
   * field in active use, not a rarely-filled extra.
   *
   * Appended rather than substituted: the external id is what reconciles against the
   * client's own system, so it stays first and keeps its place in the heading.
   */
  const headerTitle = order
    ? [
        `${t("orders.detail.title")} ${order.external_id ?? `#${order.id}`}`,
        order.title?.trim() || null,
      ]
        .filter(Boolean)
        .join(" · ")
    : t("orders.detail.title");

  return (
    <>
      <Dialog.Root
        open={open}
        onOpenChange={(next) => {
          if (next) return;
          guard.requestClose(false);
        }}
      >
        <ModalThreeColumn
          title={editMode ? t("orders.detail.editTitle") : headerTitle}
          /*
            VIEW mode only (owner, 2026-08-25): the middle column carries ДОСТАВКА +
            ГРУЗ + КЛИЕНТ + notes + ПОСЫЛКИ against a short ЖУРНАЛ timeline, so equal
            thirds starved it — «Без подтверждения» wrapped to two lines at 1440 and
            1280 alike, and the squeezed notes strip painted over the ПОСЫЛКИ header.
            EDIT mode stays on equal thirds: it is the same three-form layout as
            «Новый заказ», where the columns genuinely carry comparable weight.
          */
          wideMiddle={!editMode}
          subtitle={
            order ? (
              <>
                {t("orders.detail.createdLine", {
                  type: formatOrderType(order.order_type),
                  date: formatDateTime(order.created_at, {
                    timeZone: tenantTz,
                  }),
                })}
                {/*
                  Edit mode only. In VIEW mode the «МАРШРУТ» box at the bottom of
                  the third column carries this link (mockup, 2026-08-25), and
                  rendering it in both places puts two links with the SAME
                  accessible name and the same destination in one dialog — the
                  duplicate-name defect BUG-504/508 covers. Edit mode has no
                  route box, so there the subtitle is the only place it can live.
                */}
                {editMode && order.route_id != null && (
                  <>
                    {" · "}
                    <Link
                      to={`/routes/${order.route_id}`}
                      className="font-semibold text-brand-primary hover:underline"
                      onClick={() => onOpenChange(false)}
                    >
                      {t("orders.detail.route")} #{order.route_id}
                    </Link>
                  </>
                )}
              </>
            ) : undefined
          }
          headerActions={
            order && !editMode ? (
              <>
                {st && (
                  <span
                    className={cn(
                      "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold",
                      st.bg,
                      st.text,
                    )}
                  >
                    <span
                      className={cn("h-1.5 w-1.5 rounded-full", st.dot)}
                      aria-hidden="true"
                    />
                    {t(`orders.status.${order.status}`)}
                  </span>
                )}
                {/*
                  Owner, 2026-08-25: «кнопки Редактировать и Архив выглядят больше,
                  чем другие кнопки на странице, например Позвонить или Написать».
                  The whole header action set is `sm` (h-8 / text-xs / px-3) — the
                  page-level reference the orders page was normalised to.

                  This REPLACES the earlier TASK-4836 note, whose premise no longer
                  held. That note argued for `md` because «Позвонить»/«Написать»
                  measured 40px against the header's 32px. Measured again on order 94:
                  «Позвонить»/«Написать» are h=40 with 6px of horizontal padding,
                  while «Редактировать» was h=40 with 16px — same height, visibly
                  heavier box. Matching on `md` therefore never made the two read
                  alike; it only made the header the widest thing in the dialog. The
                  header is chrome ABOVE the record and must not out-weigh the
                  actions inside it, so it goes to the lighter step and now matches
                  the parcels controls (h-8), which are the other chrome in the body.
                */}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleEditClick}
                >
                  {t("orders.detail.edit")}
                </Button>
                {canArchive && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="gap-1.5"
                    onClick={() => setPendingTransition("archived")}
                  >
                    {/* 14px to sit with `sm`'s 12px label; `h-4` was sized for the
                        old h-10 button and now overpowers the text beside it. */}
                    <Archive className="h-3.5 w-3.5" aria-hidden="true" />
                    {t("orders.detail.archiveAction")}
                  </Button>
                )}
                {canSaveTemplate && (
                  <IconButton
                    icon={<Bookmark size={16} />}
                    label={t("orderTemplates.saveAsTemplate")}
                    variant="ghost"
                    // 32×32, one family with the `sm` text buttons beside it.
                    size="icon-sm"
                    onClick={() => setSaveTemplateOpen(true)}
                  />
                )}
                {/*
                  The destructive action keeps its OUTLINE (owner, mockup
                  2026-08-24) — that is what marks it out, and it still does so at
                  any size. The earlier TASK-4836 note argued the emphasis had to
                  come from being a size step larger than its neighbours; with the
                  whole row now at h-8 that would leave the trash as the single
                  tallest control in the header, which is precisely the "header
                  outweighs the body" complaint. Colour and border carry the
                  emphasis; size stays uniform.
                */}
                <IconButton
                  icon={<Trash2 size={16} />}
                  label={t("orders.detail.deleteOrder")}
                  variant="danger-outline"
                  size="icon-sm"
                  onClick={() => setConfirmDelete(true)}
                />
              </>
            ) : undefined
          }
          banner={
            (errors as { root?: { message?: string } }).root ? (
              <div className="rounded-lg border border-brand-danger/30 bg-brand-danger/10 px-4 py-3 text-sm text-brand-danger">
                {(errors as { root?: { message?: string } }).root?.message}
              </div>
            ) : undefined
          }
          bodyWrapper={
            editMode
              ? (columns) => (
                  <form
                    id="edit-order-form"
                    onSubmit={handleSubmit(onSubmit)}
                    className="flex min-h-0 flex-1 flex-col overflow-hidden"
                  >
                    {columns}
                  </form>
                )
              : undefined
          }
          /*
            EDIT mode only — the same hint «Новый заказ» carries, so the two
            forms read alike (owner, 2026-08-25). With parcels ON and rows that
            actually carry weight it also reports the running total, which is the
            third rendering of the number `sumParcelWeight` produces; with no
            weighed rows it falls back to the plain sentence rather than claiming
            a parcel-derived total over a weight the dispatcher typed by hand.

            The VIEW mode keeps no hint — it is a record, not a form, and it was
            just accepted as it stands.
          */
          footerHint={
            editMode
              ? weightIsComputed
                ? t("orders.form.requiredHintWithParcels", {
                    count: parcelCount,
                    weight: computedWeight,
                  })
                : t("orders.form.requiredHint")
              : undefined
          }
          footerActions={
            editMode ? (
              <>
                {/*
                  BUG-387 lived on exactly this control: `ui/Button` did not
                  default its `type`, so «Отмена» inside the form submitted it
                  instead of discarding. `Button` now defaults to
                  `type="button"` app-wide, and the explicit `type` here states
                  the intent at the call site as well.
                */}
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    if (isDirty) {
                      setConfirmCancelEdit(true);
                      return;
                    }
                    discardEdit();
                  }}
                >
                  {t("common.cancel")}
                </Button>
                <Button
                  type="submit"
                  form="edit-order-form"
                  variant="primary"
                  size="sm"
                  disabled={updateMutation.isPending}
                >
                  {updateMutation.isPending && (
                    <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  )}
                  {t("orders.detail.saveChanges")}
                </Button>
              </>
            ) : undefined
          }
        >
          {/* ─────────────────────────── column 2 ─────────────────────────── */}
          <ModalColumn
            heading={
              editMode
                ? t("orders.form.sectionOrder")
                : t("orders.detail.sectionFacts")
            }
            // View mode wants КУДА → ДОСТАВКА → ЖУРНАЛ; the JSX is ordered for the
            // edit form (ЗАКАЗ → ГРУЗ → АДРЕС), so view mode reorders visually.
            order={editMode ? undefined : 2}
            // View mode: a full-height flex column so the LAST block in it —
            // the parcels box with parcels on, the notes strip with them off —
            // finishes level with the other two columns (mockup, 2026-08-25).
            fill={!editMode}
          >
            {isLoading && <ColumnSkeleton />}

            {!isLoading && order && !editMode && (
              <>
                <OrderViewFacts
                  order={order}
                  podMethod={formatPodMethod(order)}
                  // With parcels OFF the notes strip is the last thing in the
                  // column, so it is what stretches to the bottom edge. With
                  // parcels ON the parcels block below it takes that role and
                  // the notes strip keeps its intrinsic height — otherwise both
                  // would compete for the same slack and neither would land.
                  growNotes={!parcelsEnabled}
                />

                {/* Parcels (barcodes/QR codes) — editable for dispatcher/admin.
                    Hidden entirely when the tenant parcels feature is off. */}
                {parcelsEnabled && (
                  <ParcelsEditSection
                    orderId={orderId}
                    parcels={order.parcels ?? []}
                    canEdit={canEditParcels}
                    barcodesFromOrderNumber={barcodesFromOrderNumber}
                  />
                )}

                {/* Recipient feedback — hidden entirely when none was left */}
                {order.feedback && (
                  <section className="space-y-2">
                    <ModalSubheading>
                      {t("orders.detail.feedback.title")}
                    </ModalSubheading>
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                      <RatingStars value={order.feedback.rating} />
                      <span>{order.feedback.rating}</span>
                    </div>
                    <p className="text-xs text-gray-400">
                      {t("orders.detail.feedback.date")}:{" "}
                      {formatFeedbackDate(order.feedback.created_at)}
                    </p>
                    <p className="text-xs text-gray-400">
                      {t("orders.detail.feedback.comment")}
                    </p>
                    {order.feedback.comment ? (
                      <p className="whitespace-pre-line break-words text-sm font-medium text-gray-800">
                        {order.feedback.comment}
                      </p>
                    ) : (
                      <p className="text-sm italic text-gray-400">
                        {t("orders.detail.feedback.noComment")}
                      </p>
                    )}
                  </section>
                )}

                {/* Custom fields (view mode) */}
                {customFields.length > 0 &&
                  order.custom_fields &&
                  Object.keys(order.custom_fields).length > 0 && (
                    <section className="space-y-2">
                      <ModalSubheading>
                        {t("orders.form.customFields.title")}
                      </ModalSubheading>
                      <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
                        {customFields.map((field) => {
                          const val = order.custom_fields?.[field.name];
                          if (!val) return null;
                          return (
                            <div key={field.id} className="min-w-0">
                              <dt className="text-xs text-gray-400">
                                {field.name}
                              </dt>
                              <dd className="mt-0.5 truncate text-sm font-medium text-gray-900">
                                {val}
                              </dd>
                            </div>
                          );
                        })}
                      </dl>
                    </section>
                  )}

                {/*
                  Status transitions other than Archive, which is a header action.
                  Re-queue (→ New) lives here.

                  Owner, 2026-08-24: that is what the comment always CLAIMED, but the
                  code mapped over every transition, so «→ Архив» rendered twice —
                  here and in the header — both calling `setPendingTransition`.
                  `otherTransitions` makes the code match the comment.

                  Filtering rather than deleting the whole block: for `new` and
                  `confirmed` the only transition IS archive, so the section
                  correctly disappears — but `rejected` and `skipped` also offer
                  «→ Новый», which has no header button. Deleting the section would
                  have silently removed the only way to re-queue an order.
                */}
                {otherTransitions.length > 0 && (
                  <section className="space-y-2">
                    <ModalSubheading>
                      {t("orders.detail.statusTransitions")}
                    </ModalSubheading>
                    <div className="flex flex-wrap gap-2">
                      {otherTransitions.map((transition) => {
                        const transitionLabel = t(
                          `orders.status.${transition}`,
                        );
                        const ts = STATUS_STYLE[transition];
                        return (
                          <Button
                            key={transition}
                            type="button"
                            variant="secondary"
                            size="sm"
                            onClick={() => setPendingTransition(transition)}
                            className={cn(ts.bg, ts.text, ts.border)}
                          >
                            → {transitionLabel}
                          </Button>
                        );
                      })}
                    </div>
                  </section>
                )}
              </>
            )}

            {/* ── Edit body — the TASK-4829 create layout, values loaded ── */}
            {!isLoading && order && editMode && (
              <>
                {/*
                  Same four header controls, same ids, same order in both
                  layouts — only how they PAIR on a row changes, exactly as in
                  «Новый заказ»:

                    ON  — [Название | Внешний ID] then [Дата | Тип доставки]
                    OFF — [Название           ] then [Внешний ID | Дата] then [Тип]

                  With parcels ON this column also carries «Стоимость товара» and
                  «Заметки», so the header packs tighter to pay for them.
                */}
                <div
                  className={cn(
                    "grid gap-3",
                    parcelsEnabled ? "grid-cols-2" : "grid-cols-1",
                  )}
                >
                  <div className="min-w-0">
                    <label htmlFor="edit-order-title" className={labelCls}>
                      {t("orders.form.orderName")}
                    </label>
                    <input
                      id="edit-order-title"
                      {...register("title")}
                      placeholder={t("orders.form.orderNamePlaceholder")}
                      className={inputCls}
                    />
                    {errors.title && (
                      <p className="mt-1 text-xs text-brand-danger">
                        {errors.title.message}
                      </p>
                    )}
                  </div>
                  {parcelsEnabled && externalIdField}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {!parcelsEnabled && externalIdField}
                  <div className="min-w-0">
                    <label htmlFor="edit-order-date" className={labelCls}>
                      {t("orders.detail.deliveryDate")}{" "}
                      <span className="text-brand-danger" aria-hidden="true">
                        *
                      </span>
                    </label>
                    <Controller
                      control={control}
                      name="delivery_date"
                      render={({ field }) => (
                        <DateInput
                          id="edit-order-date"
                          value={field.value ?? ""}
                          onChange={field.onChange}
                          aria-label={t("orders.detail.deliveryDate")}
                          className="py-2.5"
                        />
                      )}
                    />
                    {errors.delivery_date && (
                      <p className="mt-1 text-xs text-brand-danger">
                        {errors.delivery_date.message}
                      </p>
                    )}
                  </div>
                  {parcelsEnabled && deliveryTypeField}
                </div>

                {!parcelsEnabled && deliveryTypeField}

                {/*
                  «строго» rides on the SAME row as the time window in both
                  mockups rather than claiming a row of its own — it qualifies
                  the window, and a full row for one toggle is ~56px the height
                  budget spends better elsewhere. This is also what «Новый
                  заказ» does, and the two forms must read alike.
                */}
                <div>
                  <span className={labelCls}>
                    {t("orders.form.timeWindow")}
                  </span>
                  <div className="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)_auto] items-center gap-2">
                    <input
                      {...register("time_window_start")}
                      type="time"
                      aria-label={t("orders.detail.timeWindowStart")}
                      className={inputCls}
                    />
                    <span aria-hidden="true" className="text-gray-400">
                      —
                    </span>
                    <input
                      {...register("time_window_end")}
                      type="time"
                      aria-label={t("orders.detail.timeWindowEnd")}
                      className={inputCls}
                    />
                    <div className="flex shrink-0 items-center gap-1.5">
                      <Controller
                        control={control}
                        name="strict_time_window"
                        render={({ field }) => (
                          <Toggle
                            id="strict_time_window_edit"
                            checked={field.value ?? false}
                            onCheckedChange={field.onChange}
                            aria-label={t(
                              "orders.detail.strictTimeWindowLabel",
                            )}
                          />
                        )}
                      />
                      <label
                        htmlFor="strict_time_window_edit"
                        className="whitespace-nowrap text-xs text-gray-500"
                      >
                        {t("orders.form.strictTimeWindowShort")}
                      </label>
                    </div>
                  </div>
                  {needsRebuildNotice && (
                    /* BUG-624: «Я активировал строгое окно, но ничего не изменилось».
                       The flag persists and the solver honours it — but only at BUILD time
                       (`routes/services.py` reads it when assembling the solver payload), so
                       on a route that was already computed the plan keeps the arrival the
                       earlier solve produced. Measured on dev: 75 orders carry the flag and
                       0 of 40 strict-window stops violate their window, so the constraint
                       machinery is fine; what was missing is any sign that the change is not
                       yet in effect. A switch that looks active while being inert teaches
                       dispatchers the setting does not work. */
                    <p
                      data-testid="strict-window-rebuild-notice"
                      className="mt-1.5 text-xs text-brand-warning"
                    >
                      {t("orders.detail.strictWindowNeedsRebuild")}
                    </p>
                  )}
                </div>

                <Controller
                  name="priority"
                  control={control}
                  render={({ field }) => (
                    <PriorityField
                      id="edit-order-priority"
                      value={field.value}
                      onChange={field.onChange}
                      // Reads as an ordinary field label here, matching the
                      // fields stacked around it in the column rather than
                      // announcing a new section — as in «Новый заказ».
                      labelVariant="field"
                    />
                  )}
                />

                {/*
                  One row, mirroring «Новый заказ» (owner, 2026-08-24). Both are
                  short optional trailing fields; stacked they made the edit form
                  read differently from the create form it is supposed to match.
                */}
                <div
                  className={cn(
                    "grid items-start gap-3",
                    parcelsEnabled ? "grid-cols-2" : "grid-cols-1",
                  )}
                >
                  <div className="min-w-0">
                    <label
                      htmlFor="edit-order-service-time"
                      className={labelCls}
                    >
                      {t("orders.detail.serviceTime")}
                    </label>
                    <input
                      id="edit-order-service-time"
                      {...register("service_time_min", {
                        setValueAs: (v) => (v === "" ? undefined : Number(v)),
                      })}
                      type="number"
                      min="0"
                      className={inputCls}
                    />
                    {errors.service_time_min && (
                      <p className="mt-1 text-xs text-brand-danger">
                        {errors.service_time_min.message}
                      </p>
                    )}
                  </div>
                  {/* MOVER 1 — column 1 only when parcels are ON. */}
                  {parcelsEnabled && itemCostField}
                </div>

                <Controller
                  control={control}
                  name="tags"
                  render={({ field }) => (
                    <TagsInput
                      idPrefix="edit-order"
                      value={field.value ?? ""}
                      onChange={field.onChange}
                    />
                  )}
                />

                {/* MOVER 2 — column 1 only when parcels are ON; ends the
                    column, so it does not need to absorb slack here. */}
                {parcelsEnabled && notesField(false)}
              </>
            )}
          </ModalColumn>

          {/* ─────────────────────────── column 3 ─────────────────────────── */}
          <ModalColumn
            heading={
              editMode
                ? t("orders.form.sectionCargo")
                : t("auditLog.columnTitle")
            }
            order={editMode ? undefined : 3}
            // Both modes now: edit mode lets the driver-notes box absorb the
            // leftover height; view mode lets the ЖУРНАЛ area do the same so
            // «МАРШРУТ» can sit on the column's bottom edge (mockup, 2026-08-25)
            // instead of trailing directly under the last log entry.
            fill
          >
            {/*
              View mode: the change log, open by default for every staff role.
              The old code gated it behind `isCompanyAdmin && showAuditLog`;
              both are gone — the backend widened its role check this sprint and
              is the only gate. There is no click-to-reveal.

              `flex-1` is what pins «МАРШРУТ» to the bottom: this wrapper eats
              the column's slack, so the box after it is pushed down to the same
              line the other two columns finish on.

              `min-h-0` is what stops it eating MORE than the slack. A flex item
              defaults to `min-height: auto` — it refuses to shrink below its
              content — so an order with a long history grew this wrapper past
              the column's track and stretched the dialog with it (owner,
              2026-08-25: «если много записей … оно просто тянет модалку»; order
              119 has 37 entries). With `min-h-0` the wrapper is capped by the
              track and the LIST inside it scrolls instead.

              THE SCROLL RULE, stated once: **exactly two regions in this dialog
              may scroll — the parcels list and the audit-log list — and nothing
              else, ever.** Both are lists of unbounded length, so both need a
              bounded viewport; everything else is content of known size, and any
              other overflow is a layout bug to FIX, not to hide behind a
              scrollbar. `ModalColumn`'s own `overflow-y-auto` is a safety net
              against content being swallowed by the form's `overflow: hidden`,
              not a licence for a column to scroll: if a column ever does, the
              column's content is too tall and that is the defect.
            */}
            {!editMode && (
              <>
                <div className="flex min-h-0 flex-1 flex-col">
                  <OrderAuditColumn orderId={orderId} enabled={open} />
                </div>
                {order && (
                  <OrderRouteBox
                    order={order}
                    onNavigate={() => onOpenChange(false)}
                  />
                )}
              </>
            )}

            {!isLoading && order && editMode && (
              <>
                <CargoNumbersRow
                  idPrefix="edit-order"
                  weight={register("weight_kg")}
                  volume={register("volume_m3")}
                  pallets={register("pallets")}
                  weightError={errors.weight_kg?.message}
                  volumeError={errors.volume_m3?.message}
                  palletsError={errors.pallets?.message}
                  // Weighed parcel rows present → «Вес, кг» is their sum,
                  // read-only. Parcels merely ENABLED but unweighed leaves the
                  // field editable (owner, 2026-08-25) — see `weightIsComputed`.
                  computedWeight={weightIsComputed ? computedWeight : undefined}
                />

                {/*
                  Parcels — the block, and (with the audit log absent in edit
                  mode) the dialog's ONLY scrolling element.

                  The SAME component «Новый заказ» uses, driven by this form's
                  own `control`: the rows live in form state and are saved with
                  the order in one PATCH, so «Отмена» discards them exactly as it
                  discards a typed address. The read view's `ParcelsEditSection`
                  persists each row on blur instead — deliberately a different
                  data flow, which is why only the LEAF row layout is shared.
                */}
                {parcelsEnabled && (
                  <ParcelRowsEditor
                    control={control}
                    register={register}
                    errors={errors}
                  />
                )}

                {/* MOVER 1 — column 2 only when parcels are OFF, taking the
                    space the parcels block would otherwise occupy. */}
                {!parcelsEnabled && itemCostField}

                <ModalSubheading>
                  {t("orders.form.sectionCustomer")}
                </ModalSubheading>

                <div className="min-w-0">
                  <label
                    htmlFor="edit-order-customer-name"
                    className={labelCls}
                  >
                    {t("orders.detail.customerName")}
                  </label>
                  <input
                    id="edit-order-customer-name"
                    {...register("customer_name")}
                    className={inputCls}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="min-w-0">
                    <label
                      htmlFor="edit-order-customer-phone"
                      className={labelCls}
                    >
                      {t("orders.detail.customerPhone")}
                    </label>
                    <input
                      id="edit-order-customer-phone"
                      {...register("customer_phone")}
                      type="tel"
                      className={inputCls}
                    />
                  </div>
                  <div className="min-w-0">
                    <label
                      htmlFor="edit-order-customer-email"
                      className={labelCls}
                    >
                      {t("orders.detail.customerEmail")}
                    </label>
                    <input
                      id="edit-order-customer-email"
                      {...register("customer_email")}
                      type="email"
                      className={inputCls}
                    />
                  </div>
                </div>

                {/*
                  MOVER 2 — column 2 only when parcels are OFF. It is the last
                  block here, so it absorbs the leftover height and the column
                  ends flush with the other two instead of trailing off into dead
                  space — but only when no custom fields follow it, which a
                  growing textarea would push out of view.
                */}
                {!parcelsEnabled && notesField(customFields.length === 0)}

                {customFields.length > 0 && (
                  <div className="space-y-3">
                    <ModalSubheading>
                      {t("orders.form.customFields.title")}
                    </ModalSubheading>
                    {customFields.map((field) => (
                      <div key={field.id} className="min-w-0">
                        <label
                          htmlFor={`edit-order-custom-${field.id}`}
                          className={labelCls}
                        >
                          {field.name}
                        </label>
                        <input
                          id={`edit-order-custom-${field.id}`}
                          type="text"
                          value={customValues[field.name] ?? ""}
                          onChange={(e) => {
                            const val = e.target.value;
                            setCustomValues((prev) => ({
                              ...prev,
                              [field.name]: val,
                            }));
                          }}
                          className={inputCls}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </ModalColumn>

          {/* ─────────────────────────── column 1 ─────────────────────────── */}
          <ModalColumn
            heading={
              editMode
                ? t("orders.form.sectionAddress")
                : t("orders.detail.sectionWhere")
            }
            required={editMode}
            order={editMode ? undefined : 1}
            /*
              BOTH modes are full-height flex columns whose map absorbs the
              slack, so the three columns finish on one line.

              View mode: «ОТСЛЕЖИВАНИЕ КЛИЕНТОМ» lands level with the other two
              (mockup, 2026-08-25). Edit mode: the same treatment «Новый заказ»
              gets, so «Требовать подтверждение» sits level with «Заметки для
              водителя» and «Email клиента». Edit mode previously opted out and
              ended ~90px short of its neighbours — the create modal's own
              measured defect, fixed there and not here.
            */
            fill
          >
            {isLoading && <ColumnSkeleton />}

            {/* ── Failure state (BUG-498) ──
                The third branch the modal never had. `isError` covers a
                rejected query; `!order` also catches a resolved-but-empty
                response, so "finished loading with nothing to show" can never
                again render as a blank shell. */}
            {!isLoading && isError && !order && (
              <div
                role="alert"
                className="flex flex-col items-center gap-3 px-2 py-10 text-center"
              >
                <AlertCircle
                  className="h-10 w-10 text-gray-300"
                  aria-hidden="true"
                />
                <p className="text-sm font-semibold text-gray-900">
                  {notFound
                    ? t("orders.detail.notFoundTitle")
                    : t("orders.detail.loadFailedTitle")}
                </p>
                <p className="max-w-sm text-sm text-gray-500">
                  {notFound
                    ? t("orders.detail.notFoundBody")
                    : extractApiErrorMessage(error, t)}
                </p>
                {!notFound && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={isFetching}
                    onClick={() => void refetch()}
                  >
                    {t("common.retry")}
                  </Button>
                )}
              </div>
            )}

            {!isLoading && order && !editMode && (
              <>
                {/* `growMap`: the map is the element that absorbs this column's
                    leftover height, so the tracking box below finishes level
                    with the other two columns. */}
                <OrderViewWhere order={order} growMap />
                <CustomerTrackingSection orderId={orderId} enabled={open} />
              </>
            )}

            {!isLoading && order && editMode && (
              <AddressMapBlock
                // The map absorbs this column's leftover height, so the
                // confirmation block below it lands on the same bottom line as
                // the other two columns — as in «Новый заказ».
                growMap
                idPrefix="edit-order"
                address={watchedAddress ?? ""}
                onAddressChange={(next) =>
                  setValue("address", next, { shouldDirty: true })
                }
                lat={watchedLatStr ?? ""}
                lon={watchedLonStr ?? ""}
                onCoordsChange={(nextLat, nextLon) => {
                  setValue("lat", nextLat, { shouldDirty: true });
                  setValue("lon", nextLon, { shouldDirty: true });
                }}
                addressError={errors.address?.message}
                latError={errors.lat?.message}
                lonError={errors.lon?.message}
              >
                <ConfirmationBlock
                  idPrefix="edit-order"
                  enabled={overrideConfirmation ?? false}
                  onEnabledChange={(next) =>
                    setValue("override_confirmation", next, {
                      shouldDirty: true,
                    })
                  }
                  option={confirmationOption ?? "photo"}
                  onOptionChange={(next) =>
                    setValue("confirmation_option", next, { shouldDirty: true })
                  }
                  radius={
                    confirmationRadius != null && !isNaN(confirmationRadius)
                      ? confirmationRadius
                      : undefined
                  }
                  onRadiusChange={(next) =>
                    setValue("confirmation_radius_m", next, {
                      shouldDirty: true,
                    })
                  }
                  radiusError={errors.confirmation_radius_m?.message}
                />
              </AddressMapBlock>
            )}
          </ModalColumn>
        </ModalThreeColumn>
      </Dialog.Root>

      {/* Status-transition confirmation — «→ Архив» keeps its confirm step. */}
      <ConfirmDialog
        open={pendingTransition !== null}
        onOpenChange={(o) => {
          if (!o) setPendingTransition(null);
        }}
        title={t("orders.detail.statusTransitions")}
        description={t("orders.detail.confirmTransition", {
          label: pendingTransition
            ? t(`orders.status.${pendingTransition}`)
            : "",
        })}
        variant={pendingTransition === "archived" ? "danger" : "default"}
        confirmLabel={t("common.confirm")}
        onConfirm={() => {
          if (pendingTransition) transitionMutation.mutate(pendingTransition);
        }}
        onCancel={() => setPendingTransition(null)}
      />

      {/* Unsaved-changes guard on close (✕ / Escape / outside click). */}
      <ConfirmDialog
        open={guard.confirmOpen}
        onOpenChange={guard.setConfirmOpen}
        title={t("orders.detail.discardChanges")}
        description={t("orders.detail.discardChangesDescription")}
        variant="danger"
        onConfirm={guard.confirmDiscard}
      />

      {/* Unsaved-changes guard on «Отмена» inside edit mode. */}
      <ConfirmDialog
        open={confirmCancelEdit}
        onOpenChange={(o) => {
          if (!o) setConfirmCancelEdit(false);
        }}
        title={t("orders.detail.discardChanges")}
        description={t("orders.detail.discardChangesDescription")}
        variant="danger"
        onConfirm={() => {
          setConfirmCancelEdit(false);
          discardEdit();
        }}
        onCancel={() => setConfirmCancelEdit(false)}
      />

      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={(o) => {
          if (!o) setConfirmDelete(false);
        }}
        title={t("orders.detail.deleteOrder")}
        description={t("orders.detail.deleteConfirm")}
        variant="danger"
        confirmLabel={t("common.delete")}
        onConfirm={() => deleteMutation.mutate()}
      />

      {order && canSaveTemplate && (
        <SaveAsTemplateModal
          open={saveTemplateOpen}
          onOpenChange={setSaveTemplateOpen}
          order={order}
        />
      )}
    </>
  );
}

/** Loading placeholder for a single column. */
function ColumnSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-6 w-full" />
      ))}
    </div>
  );
}
